/**
 * Pillar 5 — Revision War Room.
 *
 * One operational surface. Answers in <60s:
 *
 *   1. What changed?               — left list of sheets w/ changes
 *   2. Where did it change?        — synchronised dual-view stage
 *   3. Why does it matter?         — every change carries severity + reasoning
 *   4. Who needs to know?          — ack roster, send-to-foreman
 *   5. Has the field acknowledged? — ack counts per sheet
 *
 * Reuses (does not rebuild):
 *   - SendToForeman component
 *   - AckPill / AckSummary
 *   - sheetRasterUrl
 *   - TrustBar can navigate here via action `open_war_room`
 *
 * Construction language only.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, ChevronRight, RefreshCw, Layers3, Columns, Zap,
  Send, AlertOctagon, AlertTriangle, FileSearch, Loader2, CheckCircle2, Clock,
} from "lucide-react";
import { api, sheetRasterUrl } from "@/lib/api";
import { AckSummary } from "@/components/AckPill";
import WhatChangedPanel from "@/components/war_room/WhatChangedPanel";
import ImpactChip from "@/components/war_room/ImpactChip";
import StoryPanels from "@/components/stories/StoryPanels";
import { confirm } from "@/lib/confirm";


export default function RevisionWarRoom() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const tOpen = useRef(performance.now());
  const [perf, setPerf] = useState({ open_ms: null, load_ms: null, jump_ms: null });

  // Project pulse
  const [warRoom, setWarRoom] = useState(null);
  const [warRoomError, setWarRoomError] = useState(null);
  // Selected sheet pair
  const [activeSheetId, setActiveSheetId] = useState(null);
  const [activePayload, setActivePayload] = useState(null);
  const [loadingActive, setLoadingActive] = useState(false);
  const [mode, setMode] = useState("overlay"); // overlay | side | blink
  const [opacity, setOpacity] = useState(0.5);
  const [blinkShowOther, setBlinkShowOther] = useState(false);
  const [focusedChangeId, setFocusedChangeId] = useState(null);
  const [sendingSummary, setSendingSummary] = useState(false);
  const stageRef = useRef(null);

  // Initial load
  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    const tStart = performance.now();
    api.get(`/projects/${projectId}/revision-war-room`).then((r) => {
      if (!alive) return;
      setWarRoom(r.data);
      const first = (r.data?.rows || [])[0];
      if (first) setActiveSheetId(first.sheet_id);
      const open_ms = Math.round(performance.now() - tOpen.current);
      const load_ms = Math.round(performance.now() - tStart);
      setPerf((p) => ({ ...p, open_ms, load_ms }));
    }).catch((e) => {
      setWarRoomError(e?.response?.data?.detail || "Couldn't load the War Room.");
    });
    return () => { alive = false; };
  }, [projectId]);

  // Load active sheet pair when selection changes
  useEffect(() => {
    if (!activeSheetId) return;
    let alive = true;
    setLoadingActive(true);
    const tStart = performance.now();
    api.get(`/sheets/${activeSheetId}/changes`).then((r) => {
      if (!alive) return;
      setActivePayload(r.data);
      const load_ms = Math.round(performance.now() - tStart);
      setPerf((p) => ({ ...p, load_ms }));
    }).catch((e) => {
      confirm.raw?.error?.(e?.response?.data?.detail || "Couldn't load changes for this sheet.");
    }).finally(() => { if (alive) setLoadingActive(false); });
    return () => { alive = false; };
  }, [activeSheetId]);

  // Blink mode timer.
  useEffect(() => {
    if (mode !== "blink") { setBlinkShowOther(false); return; }
    const id = setInterval(() => setBlinkShowOther((v) => !v), 700);
    return () => clearInterval(id);
  }, [mode]);

  const activeRow = useMemo(
    () => (warRoom?.rows || []).find((r) => r.sheet_id === activeSheetId),
    [warRoom, activeSheetId],
  );

  const currentUrl = activeSheetId ? sheetRasterUrl(activeSheetId, { dpr: 1 }) : null;
  const priorUrl = activeRow?.prior_sheet_id
    ? sheetRasterUrl(activeRow.prior_sheet_id, { dpr: 1 })
    : null;

  // Jump-to-change focus — smooth-scroll stage to bbox centre.
  const focusChange = useCallback((change) => {
    if (!change || !stageRef.current) return;
    const t0 = performance.now();
    setFocusedChangeId(change.id);
    const stage = stageRef.current;
    const content = stage.querySelector("[data-content]");
    if (!content) return;
    const cx = change.bbox.nx + change.bbox.nw / 2;
    const cy = change.bbox.ny + change.bbox.nh / 2;
    const targetLeft = Math.max(0, cx * content.clientWidth - stage.clientWidth / 2);
    const targetTop = Math.max(0, cy * content.clientHeight - stage.clientHeight / 2);
    stage.scrollTo({ left: targetLeft, top: targetTop, behavior: "smooth" });
    requestAnimationFrame(() => {
      const jump_ms = Math.round(performance.now() - t0);
      setPerf((p) => ({ ...p, jump_ms }));
    });
    setTimeout(() => setFocusedChangeId((c) => (c === change.id ? null : c)), 1800);
  }, []);

  const recompute = async () => {
    if (!activeSheetId) return;
    setLoadingActive(true);
    try {
      await api.post(`/sheets/${activeSheetId}/changes/recompute`);
      const r = await api.get(`/sheets/${activeSheetId}/changes`);
      setActivePayload(r.data);
      confirm.raw?.success?.("Re-checked for changes.");
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Recheck failed.");
    } finally {
      setLoadingActive(false);
    }
  };

  const sendRevisionSummaryToForeman = async () => {
    if (!activePayload || !activeSheetId) return;
    setSendingSummary(true);
    const t0 = performance.now();
    try {
      // 1. Server-side audit log
      await api.post(
        `/sheets/${activeSheetId}/changes/send-summary-to-foreman`,
        { summary_text: `Revision summary for ${activeRow?.sheet_number} REV ${activeRow?.current_revision}` },
      );
      // 2. Client share / clipboard fallback
      const url = `${window.location.origin}/sheet/${activeSheetId}`;
      const counts = activePayload.summary?.by_severity || {};
      const headline = [
        `${activeRow?.sheet_number || ""} REV ${activeRow?.current_revision || ""}`,
        `${activePayload.summary?.changes_total || 0} changes`,
        counts.critical ? `${counts.critical} critical` : null,
        counts.high ? `${counts.high} high` : null,
      ].filter(Boolean).join(" · ");
      const text = `Revision summary — ${headline}`;
      if (navigator.share) {
        try {
          await navigator.share({ title: "ForgedOps — revision summary", text, url });
        } catch (err) {
          if (err?.name !== "AbortError") throw err;
        }
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
      }
      const send_ms = Math.round(performance.now() - t0);
      confirm.raw?.success?.(`Sent · ${send_ms} ms`);
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSendingSummary(false);
    }
  };

  if (warRoomError) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center" data-testid="war-room-error">
        <AlertOctagon size={32} className="mx-auto text-[var(--fop-warning)]" />
        <div className="mt-2 font-semibold text-[var(--fop-text)]">War Room unavailable</div>
        <div className="mt-1 text-sm text-[var(--fop-text-3)]">{warRoomError}</div>
        <Link to="/portfolio" className="mt-4 inline-block btn btn-primary">Back to projects</Link>
      </div>
    );
  }

  if (!warRoom) {
    return (
      <div className="p-8 text-sm text-[var(--fop-text-3)] flex items-center gap-2" data-testid="war-room-loading">
        <Loader2 className="animate-spin" size={16} /> Loading the War Room…
      </div>
    );
  }

  const rows = warRoom.rows || [];
  const ack = warRoom.ack_counts || {};
  const sev = warRoom.by_severity || {};

  return (
    <div className="h-screen flex flex-col bg-[var(--fop-bg)]" data-testid="war-room">
      {/* ===== Header ===== */}
      <header className="border-b border-[var(--fop-border)] bg-[var(--fop-surface)] px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate(-1)}
          className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
          data-testid="war-room-back"
          aria-label="Back"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
            Revision War Room
          </div>
          <div className="text-sm font-semibold text-[var(--fop-text)] truncate max-w-[480px]" data-testid="war-room-project-name">
            {warRoom.project_name}
          </div>
        </div>
        {/* Demo banner removed from product surfaces per course-correction
            sprint. The flag remains on the payload for platform-admin tooling. */}

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <ImpactChip severity="critical" count={sev.critical || 0} testid="war-room-chip-critical" />
          <ImpactChip severity="high" count={sev.high || 0} testid="war-room-chip-high" />
          <ImpactChip severity="moderate" count={sev.moderate || 0} testid="war-room-chip-moderate" />
          <ImpactChip severity="low" count={sev.low || 0} testid="war-room-chip-low" />
          <div className="ml-2 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]" data-testid="war-room-ack-roll">
            <span>Field</span>
            <AckSummary counts={ack} testid="war-room-ack-summary" />
          </div>
          {perf.open_ms != null && (
            <span
              className="ml-2 text-[var(--fop-text-3)] cursor-default"
              title={`open ${perf.open_ms}ms · load ${perf.load_ms ?? "—"}ms · jump ${perf.jump_ms ?? "—"}ms`}
              data-testid="war-room-perf"
            >
              <Clock size={11} />
            </span>
          )}
        </div>
      </header>

      {/* ===== Empty state ===== */}
      {rows.length === 0 ? (
        <div className="flex-1 min-h-0 grid grid-cols-[260px_1fr]">
          <aside className="border-r border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-y-auto" data-testid="war-room-sheet-rail-empty">
            <div className="px-3 py-2 border-b border-[var(--fop-border)]">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
                Sheets with changes · 0
              </div>
            </div>
            <div className="p-3 text-[12px] text-[var(--fop-text-3)]">
              When new revisions are uploaded, the changed sheets will appear here.
            </div>
            <div className="px-3 py-2 border-t border-[var(--fop-border)]" data-testid="war-room-stories">
              <StoryPanels projectId={projectId} testidPrefix="war-room-stories-panel" />
            </div>
            <WarRoomAssetSummary projectId={projectId} />
          </aside>
          <div className="flex items-center justify-center p-10 text-center">
            <div>
              <FileSearch size={36} className="mx-auto text-[var(--fop-text-3)]" />
              <div className="mt-2 font-semibold text-[var(--fop-text)]">No revisions to review</div>
              <div className="mt-1 text-sm text-[var(--fop-text-3)] max-w-md">
                When new revisions are uploaded, the War Room will show every change,
                its field impact, and who still needs to know.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-[260px_1fr_360px]">
          {/* ===== Left: sheet rail ===== */}
          <aside className="border-r border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-y-auto" data-testid="war-room-sheet-rail">
            <div className="px-3 py-2 border-b border-[var(--fop-border)]">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
                Sheets with changes · {rows.length}
              </div>
            </div>
            {rows.map((row) => {
              const active = row.sheet_id === activeSheetId;
              return (
                <button
                  key={row.sheet_id}
                  onClick={() => setActiveSheetId(row.sheet_id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-[var(--fop-border)] flex items-center gap-2 transition-colors ${
                    active ? "bg-[var(--fop-primary-soft)]" : "hover:bg-[var(--fop-surface-2)]"
                  }`}
                  data-testid={`war-room-sheet-${row.sheet_number}`}
                  data-active={active ? "1" : "0"}
                >
                  <SeverityDot severity={row.top_severity} />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[12px] font-semibold text-[var(--fop-text)] truncate">
                      {row.sheet_number}
                    </div>
                    <div className="text-[10px] text-[var(--fop-text-3)] truncate">
                      REV {row.prior_revision} → REV {row.current_revision} · {row.changes_total} changes
                    </div>
                  </div>
                  {row.ack_counts?.pending > 0 && (
                    <span
                      className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--fop-warning)] text-[var(--fop-warning)]"
                      title={`${row.ack_counts.pending} field pending`}
                    >
                      {row.ack_counts.pending} pend
                    </span>
                  )}
                  <ChevronRight size={12} className="text-[var(--fop-text-3)] shrink-0" />
                </button>
              );
            })}
            <div className="px-3 py-2 border-t border-[var(--fop-border)]" data-testid="war-room-stories">
              <StoryPanels projectId={projectId} testidPrefix="war-room-stories-panel" />
            </div>
            <WarRoomAssetSummary projectId={projectId} />
          </aside>

          {/* ===== Center: dual-view stage ===== */}
          <main className="relative bg-white overflow-hidden flex flex-col">
            <div className="border-b border-[var(--fop-border)] bg-[var(--fop-surface-2)] px-3 py-1.5 flex items-center gap-2 flex-wrap">
              <ModeBtn label="Overlay" icon={Layers3} active={mode === "overlay"} onClick={() => setMode("overlay")} testid="war-room-mode-overlay" />
              <ModeBtn label="Side" icon={Columns} active={mode === "side"} onClick={() => setMode("side")} testid="war-room-mode-side" />
              <ModeBtn label="Blink" icon={Zap} active={mode === "blink"} onClick={() => setMode("blink")} testid="war-room-mode-blink" />
              <span className="ml-3 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                {activeRow ? `REV ${activeRow.prior_revision} vs REV ${activeRow.current_revision}` : "—"}
              </span>
              <button
                onClick={recompute}
                disabled={loadingActive || !activeSheetId}
                className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-text)] disabled:opacity-50"
                data-testid="war-room-recompute"
                title="Re-check for changes"
              >
                <RefreshCw size={11} className={loadingActive ? "animate-spin" : ""} /> Recheck
              </button>
            </div>

            <div ref={stageRef} className="flex-1 overflow-auto bg-white" data-testid="war-room-stage">
              {!activeSheetId ? (
                <div className="h-full flex items-center justify-center text-sm text-[var(--fop-text-3)]">
                  Pick a sheet to review.
                </div>
              ) : mode === "side" ? (
                <div className="grid grid-cols-2 gap-px bg-[var(--fop-border)] min-h-full" data-content>
                  <Pane title={`REV ${activeRow?.prior_revision || "?"} — Prior`} url={priorUrl} />
                  <Pane title={`REV ${activeRow?.current_revision || "?"} — Current`} url={currentUrl} accent />
                </div>
              ) : (
                <div className="relative inline-block" data-content>
                  {priorUrl && (
                    <img
                      src={priorUrl} alt="" draggable={false}
                      className="block max-w-none transition-opacity duration-150"
                      style={{
                        opacity: mode === "blink"
                          ? (blinkShowOther ? 1 : 0)
                          : (1 - opacity),
                      }}
                    />
                  )}
                  {currentUrl && (
                    <img
                      src={currentUrl} alt="" draggable={false}
                      className="block max-w-none absolute inset-0 transition-opacity duration-150"
                      style={{
                        opacity: mode === "blink"
                          ? (blinkShowOther ? 0 : 1)
                          : opacity,
                      }}
                    />
                  )}
                  {(activePayload?.changes || []).length > 0 && (
                    <svg
                      viewBox="0 0 100 100" preserveAspectRatio="none"
                      className="absolute inset-0 w-full h-full"
                      data-testid="war-room-change-overlay"
                      style={{ pointerEvents: "none" }}
                    >
                      {activePayload.changes.map((c) => (
                        <ChangeMarker
                          key={c.id} change={c}
                          focused={focusedChangeId === c.id}
                          onClick={() => focusChange(c)}
                        />
                      ))}
                    </svg>
                  )}
                </div>
              )}
            </div>

            {mode === "overlay" && activeRow && (
              <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[var(--fop-surface)]/95 border border-[var(--fop-border)] rounded-md px-3 py-1 backdrop-blur">
                <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">REV {activeRow.prior_revision}</span>
                <input
                  type="range" min={0} max={1} step={0.01}
                  value={opacity}
                  onChange={(e) => setOpacity(parseFloat(e.target.value))}
                  className="w-44 accent-[var(--fop-primary)]"
                  data-testid="war-room-opacity"
                />
                <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)]">REV {activeRow.current_revision}</span>
              </div>
            )}
          </main>

          {/* ===== Right: What Changed panel ===== */}
          <aside className="border-l border-[var(--fop-border)] bg-[var(--fop-surface)] flex flex-col min-h-0" data-testid="war-room-changes-aside">
            <WhatChangedPanel
              loading={loadingActive}
              payload={activePayload}
              activeRow={activeRow}
              focusedId={focusedChangeId}
              onFocus={focusChange}
            />
            <div className="border-t border-[var(--fop-border)] p-3 space-y-2">
              <button
                onClick={sendRevisionSummaryToForeman}
                disabled={!activePayload?.changes?.length || sendingSummary}
                className="w-full btn btn-primary text-[12px] flex items-center justify-center gap-2"
                data-testid="war-room-send-summary"
              >
                <Send size={12} />
                {sendingSummary ? "Sending…" : "Send revision summary to foreman"}
              </button>
              <Link
                to={`/sheet/${activeSheetId}/compare`}
                className="block text-center text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                data-testid="war-room-open-compare"
              >
                Open full compare →
              </Link>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}


/* ===================================================================== */
/* Sub-components                                                        */
/* ===================================================================== */

function SeverityDot({ severity }) {
  const colour = SEVERITY_COLOUR[severity] || "var(--fop-text-3)";
  return (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
      style={{ background: colour }}
      title={severity || "no impact"}
    />
  );
}


function ModeBtn({ label, icon: Icon, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono uppercase tracking-wider transition-colors border ${
        active
          ? "border-[var(--fop-primary)]/60 bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]"
          : "border-transparent text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:border-[var(--fop-border)]"
      }`}
    >
      <Icon size={11} />{label}
    </button>
  );
}


function Pane({ title, url, accent }) {
  return (
    <div className="flex flex-col overflow-hidden bg-white">
      <div
        className={`px-3 py-1 border-b border-[var(--fop-border)] font-mono text-[10px] uppercase tracking-[0.2em] ${
          accent ? "text-[var(--fop-primary)]" : "text-[var(--fop-text-3)]"
        }`}
      >
        {title}
      </div>
      <div className="flex-1 overflow-auto bg-white flex items-center justify-center">
        {url ? <img src={url} alt="" className="max-w-full max-h-full object-contain" /> : (
          <span className="text-[var(--fop-text-3)] text-xs">No revision selected.</span>
        )}
      </div>
    </div>
  );
}


function ChangeMarker({ change, focused, onClick }) {
  const colour = SEVERITY_COLOUR[change.severity] || "rgba(99,102,241,0.6)";
  const stroke = SEVERITY_STROKE[change.severity] || colour;
  const x = change.bbox.nx * 100;
  const y = change.bbox.ny * 100;
  const w = change.bbox.nw * 100;
  const h = change.bbox.nh * 100;
  return (
    <g
      onClick={onClick}
      style={{ pointerEvents: "auto", cursor: "pointer" }}
    >
      <rect
        x={x} y={y} width={w} height={h}
        fill={colour} fillOpacity={focused ? 0.32 : 0.18}
        stroke={stroke} strokeWidth={focused ? 0.5 : 0.25}
        strokeDasharray="0.6 0.4"
        vectorEffect="non-scaling-stroke"
        data-testid={`war-room-marker-${change.id}`}
      />
    </g>
  );
}


// Track 4 — War Room asset summary tile. Reads the intelligence rollup
// and surfaces conflict / needs-review counts so the operator sees the
// drawing-level impact at a glance.
function WarRoomAssetSummary({ projectId }) {
  const [intel, setIntel] = React.useState(null);
  React.useEffect(() => {
    if (!projectId) return;
    let alive = true;
    api.get(`/projects/${projectId}/assets/intelligence`)
      .then((r) => alive && setIntel(r.data))
      .catch(() => alive && setIntel(null));
    return () => { alive = false; };
  }, [projectId]);
  if (!intel) return null;
  const byStatus = Object.fromEntries((intel.by_status || []).map((b) => [b.status, b.count]));
  const total = intel.total || 0;
  return (
    <div className="px-3 py-2 border-t border-[var(--fop-border)]" data-testid="war-room-assets">
      <div className="flex items-center gap-2 mb-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-2)] font-semibold">Affected Assets</div>
        <div className="ml-auto font-mono text-[10px] tabular-nums text-[var(--fop-text-3)]" data-testid="war-room-assets-total">{total}</div>
      </div>
      {total === 0 ? (
        <div className="text-[12px] text-[var(--fop-text-3)]">No assets placed on this project yet.</div>
      ) : (
        <div className="grid grid-cols-3 gap-1.5 text-[11px]">
          <Tile label="Conflict" value={byStatus.conflict || 0} colour="#dc2626" testid="war-room-assets-conflict" />
          <Tile label="Needs review" value={byStatus.needs_review || 0} colour="#f97316" testid="war-room-assets-review" />
          <Tile label="Waiting" value={byStatus.waiting_for_field || 0} colour="#f59e0b" testid="war-room-assets-waiting" />
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, colour, testid }) {
  return (
    <div
      className="rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] px-2 py-1.5 flex flex-col items-center"
      data-testid={testid}
    >
      <span className="font-mono text-base tabular-nums font-bold" style={{ color: value ? colour : "var(--fop-text-3)" }}>{value}</span>
      <span className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)]">{label}</span>
    </div>
  );
}


export const SEVERITY_COLOUR = {
  critical: "rgba(220, 38, 38, 0.65)",
  high:     "rgba(245, 158, 11, 0.55)",
  moderate: "rgba(249, 115, 22, 0.45)",
  low:      "rgba(56, 189, 248, 0.40)",
  no:       "rgba(148, 163, 184, 0.35)",
};
export const SEVERITY_STROKE = {
  critical: "#dc2626",
  high:     "#f59e0b",
  moderate: "#f97316",
  low:      "#38bdf8",
  no:       "#94a3b8",
};
